import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, BookOpen, DollarSign, TrendingUp, Search, Ban, Star, GraduationCap, LogOut,
  CreditCard, BarChart3, LayoutDashboard, ShieldCheck,
  Eye,  Plus, ChevronRight, Award,
 Activity, Settings, UserCog, 
} from 'lucide-react';
import AdminCourseApprovals from '@/components/admin/AdminCourseApproval';
import AdminSystemSettings from '@/components/admin/AdminSystemSettings';
import AdminActivityLog from '@/components/admin/AdminActivityLogs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { COURSES, ADMIN_USERS, ADMIN_TRANSACTIONS, clearUser } from '@/lib/data';

const userGrowth = [
  { month: 'Oct', users: 2800 }, { month: 'Nov', users: 3400 },
  { month: 'Dec', users: 4100 }, { month: 'Jan', users: 5200 },
  { month: 'Feb', users: 6800 }, { month: 'Mar', users: 8500 },
];

const coursePerformance = [
  { name: 'Web Dev', students: 18500, revenue: 7770000 },
  { name: 'AIML', students: 14200, revenue: 6671800 },
  { name: 'Gen AI', students: 9800, revenue: 3233020 },
  { name: 'Mobile', students: 7800, revenue: 3275800 },
  { name: 'Cloud', students: 4200, revenue: 1595800 },
];

const courseStudents = [
  { name: 'Aisha Patel', email: 'aisha@email.com', progress: 78, lastActive: '2 hours ago', status: 'active' },
  { name: 'Marcus Johnson', email: 'marcus@email.com', progress: 45, lastActive: '1 day ago', status: 'active' },
  { name: 'Lisa Chang', email: 'lisa@email.com', progress: 100, lastActive: '3 days ago', status: 'completed' },
  { name: 'Raj Gupta', email: 'raj@email.com', progress: 20, lastActive: '5 days ago', status: 'inactive' },
];

const courseReviews = [
  { name: 'Aisha Patel', rating: 5, text: 'Best course ever! Very practical.', date: '2 weeks ago' },
  { name: 'Marcus Johnson', rating: 4, text: 'Great content, highly recommend.', date: '1 month ago' },
];

const instructorStats = [
  { name: 'Dr. Priya Sharma', courses: 2, students: 24000, revenue: 9905980, rating: 4.9 },
  { name: 'Arjun Mehta', courses: 1, students: 6500, revenue: 2729935, rating: 4.9 },
  { name: 'Vikram Singh', courses: 1, students: 7200, revenue: 3383928, rating: 4.8 },
  { name: 'Rahul Desai', courses: 1, students: 18500, revenue: 7769815, rating: 4.8 },
  { name: 'Sneha Iyer', courses: 1, students: 20000, revenue: 5799800, rating: 4.8 },
];

export default function AdminDashboard() {
  const [userSearch, setUserSearch] = useState('');
  const [selectedSection, setSelectedSection] = useState('overview');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedInstructor, setSelectedInstructor] = useState(null);

  const filteredUsers = ADMIN_USERS.filter(u =>
    u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
    u.email.toLowerCase().includes(userSearch.toLowerCase())
  );

  const students = ADMIN_USERS.filter(u => u.role === 'student');
  const instructors = ADMIN_USERS.filter(u => u.role === 'instructor');

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-60 bg-card border-r flex-col fixed top-0 left-0 bottom-0 z-40">
        <div className="p-5 border-b">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold">Admin Panel</span>
          </div>
          <Badge className="mt-2 text-xs bg-red-100 text-red-700 border-0">Super Admin</Badge>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {[
            { id: 'overview', label: 'Overview', icon: LayoutDashboard },
            { id: 'users', label: 'Users & Roles', icon: UserCog },
            { id: 'students', label: 'Student Dashboard', icon: GraduationCap },
            { id: 'instructors', label: 'Instructor Dashboard', icon: Award },
            { id: 'courses', label: 'Course Approvals', icon: BookOpen },
            { id: 'payments', label: 'Payments', icon: CreditCard },
            { id: 'reports', label: 'Reports', icon: BarChart3 },
            { id: 'activity', label: 'Activity Log', icon: Activity },
            { id: 'settings', label: 'System Settings', icon: Settings },
          ].map(item => {
            const Icon = item.icon;
            return (
              <button key={item.id} onClick={() => { setSelectedSection(item.id); setSelectedCourse(null); setSelectedInstructor(null); }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  selectedSection === item.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
            <Eye className="w-4 h-4" /> View Site
          </Link>
          <button onClick={() => { clearUser(); window.location.href = '/'; }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-60">
        {/* Mobile Header */}
        <div className="lg:hidden bg-card border-b px-4 py-3 flex items-center justify-between sticky top-0 z-30">
          <span className="font-heading font-bold">Admin Panel</span>
          <div className="flex gap-2">
            <Link to="/"><Button variant="ghost" size="sm">Site</Button></Link>
            <Button variant="ghost" size="icon" onClick={() => { clearUser(); window.location.href = '/'; }}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-6xl">

          {/* ========== OVERVIEW ========== */}
          {selectedSection === 'overview' && (
            <>
              <h1 className="font-heading text-2xl font-bold">Dashboard Overview</h1>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Users', value: '8,547', icon: Users, change: '+12%', color: 'text-primary bg-primary/10' },
                  { label: 'Total Courses', value: COURSES.length.toString(), icon: BookOpen, change: '+2', color: 'text-accent bg-accent/10' },
                  { label: 'Revenue', value: '₹2.84Cr', icon: DollarSign, change: '+23%', color: 'text-green-600 bg-green-100' },
                  { label: 'Active Users', value: '2,341', icon: TrendingUp, change: '+8%', color: 'text-purple-600 bg-purple-100' },
                ].map(stat => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="bg-card rounded-xl border p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
                        <span className="text-xs text-green-600 font-medium">{stat.change}</span>
                      </div>
                      <p className="font-heading text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl border p-6">
                  <h3 className="font-heading font-semibold mb-4">User Growth</h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={userGrowth}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="users" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.1)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-card rounded-xl border p-6">
                  <h3 className="font-heading font-semibold mb-4">Top Courses by Students</h3>
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={coursePerformance}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="students" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              {/* Quick Access */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { label: 'Student Dashboard', desc: 'View all student data & progress', icon: GraduationCap, id: 'students', color: 'bg-blue-50 text-blue-700' },
                  { label: 'Instructor Dashboard', desc: 'Manage courses & instructors', icon: Award, id: 'instructors', color: 'bg-purple-50 text-purple-700' },
                  { label: 'Payments', desc: 'Transactions & revenue', icon: CreditCard, id: 'payments', color: 'bg-green-50 text-green-700' },
                ].map(card => {
                  const Icon = card.icon;
                  return (
                    <button key={card.id} onClick={() => setSelectedSection(card.id)}
                      className="bg-card rounded-xl border p-5 text-left hover:shadow-md transition-shadow flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{card.label}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{card.desc}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground mt-0.5" />
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* ========== USERS & ROLES ========== */}
          {selectedSection === 'users' && (
            <>
              <div className="flex items-center justify-between">
                <h1 className="font-heading text-2xl font-bold">Users & Role Management</h1>
                <Button className="gap-2"><Plus className="w-4 h-4" /> Invite User</Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Students', value: filteredUsers.filter(u => u.role === 'student').length, color: 'text-primary bg-primary/10', icon: GraduationCap },
                  { label: 'Instructors', value: filteredUsers.filter(u => u.role === 'instructor').length, color: 'text-purple-600 bg-purple-100', icon: Award },
                  { label: 'Blocked', value: filteredUsers.filter(u => u.status === 'blocked').length, color: 'text-red-600 bg-red-100', icon: Ban },
                ].map(s => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="bg-card rounded-xl border p-4 flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center`}><Icon className="w-4 h-4" /></div>
                      <div>
                        <p className="font-heading text-xl font-bold">{s.value}</p>
                        <p className="text-xs text-muted-foreground">{s.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users..." className="pl-10" />
              </div>
              <div className="bg-card rounded-xl border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Enrolled</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map(user => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">{user.email}</TableCell>
                        <TableCell>
                          <select defaultValue={user.role}
                            className="text-xs rounded-md border bg-background px-2 py-1 font-medium capitalize">
                            <option value="student">Student</option>
                            <option value="instructor">Instructor</option>
                            <option value="admin">Admin</option>
                          </select>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs border-0 ${user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{user.status}</Badge>
                        </TableCell>
                        <TableCell>{user.enrolled}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7"><Eye className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" title="Block user"><Ban className="w-3.5 h-3.5" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {/* ========== STUDENT DASHBOARD ========== */}
          {selectedSection === 'students' && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <h1 className="font-heading text-2xl font-bold">Student Dashboard</h1>
                <Badge className="bg-primary/10 text-primary border-0 text-xs">Admin View</Badge>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Students', value: students.length.toString(), icon: Users, color: 'text-primary bg-primary/10' },
                  { label: 'Active Students', value: students.filter(s => s.status === 'active').length.toString(), icon: TrendingUp, color: 'text-green-600 bg-green-100' },
                  { label: 'Total Enrollments', value: students.reduce((s, u) => s + u.enrolled, 0).toString(), icon: BookOpen, color: 'text-accent bg-accent/10' },
                  { label: 'Avg Courses/Student', value: (students.reduce((s, u) => s + u.enrolled, 0) / students.length).toFixed(1), icon: Award, color: 'text-purple-600 bg-purple-100' },
                ].map(stat => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="bg-card rounded-xl border p-4">
                      <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
                      <p className="font-heading text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  );
                })}
              </div>
              <div className="bg-card rounded-xl border p-5 space-y-3">
                <h3 className="font-semibold">All Students</h3>
                {courseStudents.map((s, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary text-sm">{s.name[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{s.name}</p>
                      <p className="text-xs text-muted-foreground">{s.email}</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${s.progress}%` }} />
                      </div>
                      <span className="text-xs font-medium w-8">{s.progress}%</span>
                    </div>
                    <Badge className={`text-xs border-0 ${
                      s.status === 'completed' ? 'bg-green-100 text-green-700' :
                      s.status === 'inactive' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                    }`}>{s.status}</Badge>
                    <span className="text-xs text-muted-foreground hidden md:block">{s.lastActive}</span>
                  </div>
                ))}
              </div>
              {/* Student enrollment chart */}
              <div className="bg-card rounded-xl border p-6">
                <h3 className="font-heading font-semibold mb-4">Student Enrollment Trend</h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={userGrowth}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="users" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.1)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {/* ========== INSTRUCTOR DASHBOARD ========== */}
          {selectedSection === 'instructors' && !selectedCourse && !selectedInstructor && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-purple-600" />
                <h1 className="font-heading text-2xl font-bold">Instructor Dashboard</h1>
                <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">Admin View</Badge>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total Instructors', value: '12', icon: Award, color: 'text-purple-600 bg-purple-100' },
                  { label: 'Total Courses', value: COURSES.length.toString(), icon: BookOpen, color: 'text-primary bg-primary/10' },
                  { label: 'Total Revenue', value: '₹2.84Cr', icon: DollarSign, color: 'text-green-600 bg-green-100' },
                  { label: 'Avg Rating', value: '4.8', icon: Star, color: 'text-amber-600 bg-amber-100' },
                ].map(stat => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.label} className="bg-card rounded-xl border p-4">
                      <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
                      <p className="font-heading text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Instructor list */}
              <div className="bg-card rounded-xl border p-5">
                <h3 className="font-semibold mb-4">Instructors</h3>
                <div className="space-y-3">
                  {instructorStats.map((inst, i) => (
                    <button key={i} onClick={() => setSelectedInstructor(inst)}
                      className="w-full flex items-center gap-4 p-3 rounded-lg bg-muted/30 hover:bg-muted/60 transition-colors text-left">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center font-bold text-purple-700">{inst.name[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{inst.name}</p>
                        <p className="text-xs text-muted-foreground">{inst.courses} courses · {inst.students.toLocaleString()} students</p>
                      </div>
                      <div className="text-right hidden sm:block">
                        <p className="text-sm font-bold text-green-600">₹{(inst.revenue/100000).toFixed(1)}L</p>
                        <div className="flex items-center gap-0.5 justify-end mt-0.5">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          <span className="text-xs font-medium">{inst.rating}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Courses list with assign instructor */}
              <div className="bg-card rounded-xl border p-5">
                <h3 className="font-semibold mb-4">All Courses — Assign Instructors</h3>
                <div className="space-y-3">
                  {COURSES.map(course => (
                    <div key={course.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{course.title}</p>
                        <p className="text-xs text-muted-foreground">{course.students.toLocaleString()} students · ₹{course.price.toLocaleString('en-IN')}</p>
                      </div>
                      <select defaultValue={course.instructor.name}
                        className="hidden sm:block text-xs rounded-md border bg-background px-2 py-1.5 max-w-[160px] truncate">
                        {instructorStats.map(inst => (
                          <option key={inst.name} value={inst.name}>{inst.name}</option>
                        ))}
                      </select>
                      <button onClick={() => setSelectedCourse(course)} className="flex-shrink-0">
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Instructor Detail */}
          {selectedSection === 'instructors' && selectedInstructor && (
            <>
              <button onClick={() => setSelectedInstructor(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
                <ChevronRight className="w-3 h-3 rotate-180" /> Back to Instructors
              </button>
              <h1 className="font-heading text-xl font-bold">{selectedInstructor.name}</h1>
              <div className="grid sm:grid-cols-4 gap-4">
                {[
                  { label: 'Courses', value: selectedInstructor.courses, color: 'text-primary' },
                  { label: 'Students', value: selectedInstructor.students.toLocaleString(), color: 'text-blue-600' },
                  { label: 'Revenue', value: `₹${(selectedInstructor.revenue/100000).toFixed(1)}L`, color: 'text-green-600' },
                  { label: 'Rating', value: `${selectedInstructor.rating} ★`, color: 'text-amber-600' },
                ].map(m => (
                  <div key={m.label} className="bg-card rounded-xl border p-4 text-center">
                    <p className={`font-heading text-2xl font-bold ${m.color}`}>{m.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{m.label}</p>
                  </div>
                ))}
              </div>
              <div className="bg-card rounded-xl border p-5">
                <h3 className="font-semibold mb-3">Courses by this Instructor</h3>
                <div className="space-y-2">
                  {COURSES.filter(c => c.instructor.name === selectedInstructor.name).map(course => (
                    <button key={course.id} onClick={() => { setSelectedCourse(course); setSelectedInstructor(null); }}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/60 transition-colors text-left">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{course.title}</p>
                        <p className="text-xs text-muted-foreground">{course.students.toLocaleString()} students · ₹{course.price.toLocaleString('en-IN')}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Course Detail (Instructor section) */}
          {selectedSection === 'instructors' && selectedCourse && (
            <>
              <button onClick={() => setSelectedCourse(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2">
                <ChevronRight className="w-3 h-3 rotate-180" /> Back
              </button>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h1 className="font-heading text-xl font-bold">{selectedCourse.title}</h1>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">{selectedCourse.level}</Badge>
                    <span className="text-sm text-muted-foreground">{selectedCourse.instructor.name}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-green-600 text-xl">₹{selectedCourse.price.toLocaleString('en-IN')}</p>
                  <p className="text-xs text-muted-foreground line-through">₹{selectedCourse.originalPrice.toLocaleString('en-IN')}</p>
                </div>
              </div>
              <Tabs defaultValue="students">
                <TabsList>
                  <TabsTrigger value="students">Students</TabsTrigger>
                  <TabsTrigger value="curriculum">Curriculum</TabsTrigger>
                  <TabsTrigger value="reviews">Reviews</TabsTrigger>
                </TabsList>
                <TabsContent value="students" className="mt-3 space-y-2">
                  {courseStudents.map((s, i) => (
                    <div key={i} className="flex items-center gap-4 p-3 bg-card rounded-lg border">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">{s.name[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{s.name}</p>
                        <p className="text-xs text-muted-foreground">{s.lastActive}</p>
                      </div>
                      <div className="hidden sm:flex items-center gap-2">
                        <div className="w-20 h-1.5 bg-muted rounded-full">
                          <div className="h-full bg-primary rounded-full" style={{ width: `${s.progress}%` }} />
                        </div>
                        <span className="text-xs font-medium">{s.progress}%</span>
                      </div>
                      <Badge className={`text-xs border-0 ${s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'inactive' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>{s.status}</Badge>
                    </div>
                  ))}
                </TabsContent>
                <TabsContent value="curriculum" className="mt-3 space-y-2">
                  {selectedCourse.curriculum.map((mod, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-card rounded-lg border">
                      <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{i + 1}</div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{mod.module}</p>
                        <p className="text-xs text-muted-foreground">{mod.lessons} lessons · {mod.duration}</p>
                      </div>
                    </div>
                  ))}
                </TabsContent>
                <TabsContent value="reviews" className="mt-3 space-y-3">
                  {courseReviews.map((r, i) => (
                    <div key={i} className="p-4 bg-card rounded-lg border">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-sm">{r.name}</p>
                        <span className="text-xs text-muted-foreground">{r.date}</span>
                      </div>
                      <div className="flex gap-0.5 mb-1">
                        {[...Array(r.rating)].map((_, j) => <Star key={j} className="w-3 h-3 fill-amber-400 text-amber-400" />)}
                      </div>
                      <p className="text-sm text-muted-foreground">{r.text}</p>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </>
          )}

          {/* ========== COURSES (APPROVALS) ========== */}
          {selectedSection === 'courses' && <AdminCourseApprovals />}

          {/* ========== PAYMENTS ========== */}
          {selectedSection === 'payments' && (
            <>
              <h1 className="font-heading text-2xl font-bold">Payments & Transactions</h1>
              <div className="bg-card rounded-xl border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Course</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ADMIN_TRANSACTIONS.map(tx => (
                      <TableRow key={tx.id}>
                        <TableCell className="font-mono text-xs">{tx.id}</TableCell>
                        <TableCell>{tx.user}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{tx.course}</TableCell>
                        <TableCell className="font-medium">₹{tx.amount.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-muted-foreground">{new Date(tx.date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge className={`text-xs border-0 ${tx.status === 'completed' ? 'bg-green-100 text-green-700' : tx.status === 'refunded' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {tx.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {tx.status === 'completed' && <Button variant="ghost" size="sm" className="text-xs">Refund</Button>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          {/* ========== ACTIVITY ========== */}
          {selectedSection === 'activity' && <AdminActivityLog />}

          {/* ========== SETTINGS ========== */}
          {selectedSection === 'settings' && <AdminSystemSettings />}

          {/* ========== REPORTS ========== */}
          {selectedSection === 'reports' && (
            <>
              <h1 className="font-heading text-2xl font-bold">Reports & Analytics</h1>
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl border p-6">
                  <h3 className="font-heading font-semibold mb-4">User Growth</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={userGrowth}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="users" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.1)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-card rounded-xl border p-6">
                  <h3 className="font-heading font-semibold mb-4">Course Performance</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={coursePerformance}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="students" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-card rounded-xl border p-6 lg:col-span-2">
                  <h3 className="font-heading font-semibold mb-4">Revenue by Course (₹)</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={coursePerformance}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `₹${(v/100000).toFixed(0)}L`} />
                        <Tooltip formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
                        <Bar dataKey="revenue" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </motion.div>
  );
}